import { createFileRoute, redirect, stripSearchParams, useNavigate } from '@tanstack/react-router'
import { z } from 'zod'
import { RadarEventWhaleOrdersPage } from '~/features/opportunity-radar/detail-components'
import { radarEventDetailQueryOptions, whaleOrdersQueryOptions } from '~/features/opportunity-radar/query-options'
import { authRecoveryHref } from '~/lib/auth/redirect'
import { createSessionQuery } from '~/lib/session-query'

const whaleOrderDefaultSearch = {
  marketSection: 'all' as const,
  windowMinutes: undefined as number | undefined,
  lineValue: undefined as string | undefined,
}

const whaleOrderSearchSchema = z.object({
  marketSection: z.enum(['all', 'moneyline', 'spread', 'total_goals', 'prop']).optional().default(whaleOrderDefaultSearch.marketSection),
  windowMinutes: z.coerce.number().int().positive().optional(),
  lineValue: z.string().optional(),
})

export const Route = createFileRoute('/radar/events/$eventId/whale-orders')({
  validateSearch: whaleOrderSearchSchema.parse,
  search: {
    middlewares: [stripSearchParams(whaleOrderDefaultSearch)],
  },
  beforeLoad: async ({ context, location }) => {
    const session = await context.queryClient.ensureQueryData(
      createSessionQuery(context.queryClient),
    )
    if (!session?.session) {
      throw redirect({
        href: authRecoveryHref(location),
        replace: true,
      })
    }
    return { session, user: session.user }
  },
  loader: async ({ context, params, location }) => {
    await context.queryClient.ensureQueryData(radarEventDetailQueryOptions(params.eventId))
    const search = whaleOrderSearchSchema.parse(location.search)
    await context.queryClient.prefetchQuery(whaleOrdersQueryOptions({
      eventIds: params.eventId,
      marketSection: search.marketSection === 'all' ? undefined : search.marketSection,
      lineValue: search.lineValue,
      windowMinutes: search.windowMinutes,
    }))
  },
  component: WhaleOrdersRoute,
})

function WhaleOrdersRoute() {
  const { eventId } = Route.useParams()
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })

  return (
    <RadarEventWhaleOrdersPage
      eventId={eventId}
      search={search}
      onSearchChange={(nextSearch) => {
        void navigate({ search: current => whaleOrderSearchSchema.parse({ ...current, ...nextSearch }) })
      }}
    />
  )
}
